/**
 * Normalize a URL for SEO output (canonical, og:url):
 *   1. Strip query string and fragment.
 *   2. Append a trailing slash unless the last path segment ends in a file
 *      extension — mirrors the redirect carve-out in apps/core-mkt/server.ts
 *      (`/\.[^/]+$/`) so file URLs aren't mangled.
 *
 * Falsy input is returned unchanged so callers can keep their truthiness guards.
 */
export function normalizeSeoUrl(url: string): string {
  if (!url) {
    return url;
  }

  try {
    const parsed = new URL(url);
    parsed.search = '';
    parsed.hash = '';
    let { pathname } = parsed;
    if (!hasFileExtension(pathname) && !pathname.endsWith('/')) {
      pathname = `${pathname}/`;
    }
    parsed.pathname = pathname;
    return parsed.toString();
  } catch {
    const cleaned = url.split('?')[0].split('#')[0];
    if (!cleaned || hasFileExtension(cleaned) || cleaned.endsWith('/')) {
      return cleaned;
    }
    return `${cleaned}/`;
  }
}

function hasFileExtension(path: string): boolean {
  const lastSegment = path.split('/').pop() ?? '';
  return /\.[^/]+$/.test(lastSegment);
}

/**
 * Extract the pagination page number from a URL or path's `page` query param.
 *
 * Returns the integer page only when it is >= 2 — i.e. a page that warrants its
 * own distinct paginated URL. Page 1, zero (EPS-4099 paginator bug), negative,
 * non-numeric, or missing values all return null so callers emit the bare root
 * URL. `normalizeSeoUrl` strips the query string, so this is the only place the
 * `?page=N` signal survives for canonical / og:url construction (EPS-4097).
 */
export function getPaginationPage(url: string): number | null {
  if (!url) {
    return null;
  }

  let search: string;
  try {
    search = new URL(url).search;
  } catch {
    // Relative path (the usual router.url case): take everything after `?`,
    // dropping any trailing `#fragment` so it isn't parsed into the page value.
    const queryIndex = url.indexOf('?');
    search = queryIndex === -1 ? '' : url.slice(queryIndex).split('#')[0];
  }

  const raw = new URLSearchParams(search).get('page');
  if (raw === null) {
    return null;
  }

  const page = Number(raw);
  return Number.isInteger(page) && page >= 2 ? page : null;
}

/**
 * Append a `page=N` query param to a SEO URL.
 *
 * No-op unless `page` is an integer >= 2 (the only values that warrant a
 * distinct paginated URL) and the URL is truthy — so null, page 1, zero,
 * negatives, and non-integers are all ignored even if the helper is reused
 * outside its current call site. Chooses the separator defensively — `?` for a
 * clean URL, `&` if one already carries a query string. In practice the only
 * caller passes output from `normalizeSeoUrl` (query- and fragment-free), so
 * the `?` branch is what runs; the `&` branch keeps the helper correct for any
 * other caller. Used to make canonical and og:url self-referencing on paginated
 * listing pages so the two tags stay byte-identical (EPS-4097).
 */
export function appendPaginationParam(normalizedUrl: string, page: number | null): string {
  if (!normalizedUrl || page === null || !Number.isInteger(page) || page < 2) {
    return normalizedUrl;
  }

  const separator = normalizedUrl.includes('?') ? '&' : '?';
  return `${normalizedUrl}${separator}page=${page}`;
}
